import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DailyLogOperationInstance,
  DailyOperationLogTimeEditor,
  OperationType,
} from '../../models/projects/operation.models';
import {
  ActionType,
  CommentTimeError,
  ContractualTimeGroup,
  ErrorType,
  HierarchyCollision,
  StartAndEnd,
  StepTimeError,
  ValidationResult,
} from '../../models/projects/validation.models';
import { DateService } from '../date/date.service';
import { OperationService } from './operation/operation.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
// tslint:disable-next-line:max-line-length
import {
  ContractualOperationValidatorService
} from './operation-validation/contractual-operation-validator-service/contractual-operation-validator.service';
// tslint:disable-next-line:max-line-length
import {
  ConcurrentOperationValidatorService
} from './operation-validation/concurrent-operation-validator-service/concurrent-operation-validator.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { MultipleDayService } from '../mutiple-day/multiple-day.service';
import { cloneDeep } from 'lodash';
import { AppDataSyncService } from '../app-data-sync/app-data-sync.service';
import { DailyLogService } from '../daily-log/daily-log.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { SetupService } from '../setup/setup.service';
import { SetupChangeLogRecord, StageStartAndEnd } from '../../models/setup/setup.models';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';

@Injectable()
export class OperationValidatorService {

  config = AppConfig.settings;
  second: number = 1000;

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private operationService: OperationService,
    private contractualOperationValidationService: ContractualOperationValidatorService,
    private concurrentOperationValidationService: ConcurrentOperationValidatorService,
    private multipleDayService: MultipleDayService,
    private appDataSyncService: AppDataSyncService,
    private dailyLogService: DailyLogService,
    private timeZoneService: TimeZoneService,
    private setupService: SetupService,
  ) {
  }

  validateTime(editor: DailyOperationLogTimeEditor): ValidationResult {

    const validationResult = new ValidationResult();
    validationResult.isValid = true;

    if (!editor.endTime) {
      validationResult.addError(`'End Time' is required`, ErrorType.Default);
    }

    if (!editor.startTime) {
      validationResult.addError(`'Start Time' is required`, ErrorType.Default);
    }

    return validationResult;
  }

  validateTimings(operationInstance: DailyLogOperationInstance): ValidationResult {

    const validationResult = new ValidationResult();
    validationResult.isValid = true;

    if (operationInstance.startTime.dateTime > operationInstance.endTime.dateTime) {
      validationResult.addError('An operation start time cannot be later than an operation end time', ErrorType.Default);
    }

    if (!!operationInstance.stepCollectionInstance) {
      return validationResult;
    }

    const steps = operationInstance.stepCollectionInstance.stepInstances;

    steps.forEach(element => {
      if (element.startTime.dateTime > element.endTime.dateTime) {
        validationResult.addError(`Step: '` + element.step.name +
          `' has a start time greater than it's end time. This is not allowed`, ErrorType.Default);
      }
    });

    return validationResult;

  }

  async validateOperation(
    instance: DailyLogOperationInstance,
    mostRecentTime: PtLuxonDate,
    dailyLogDate: PtLuxonDate,
    type?: OperationType,
    validationResult?: ValidationResult
  ): Promise<ValidationResult> {
    if (!validationResult) {
      validationResult = new ValidationResult();
    }

    let oldDate = !instance || !instance.startTime || !instance.startTime.dateTime ? cloneDeep(mostRecentTime) as PtLuxonDate : cloneDeep(instance.startTime) as PtLuxonDate;

    if (!oldDate || !oldDate.dateTime || !oldDate.dateTime.toMillis()) {
      oldDate = new PtLuxonDate();
    }

    let otherOperations = await this.operationService.getAllProjectSpecificOperationInstancesForDay(oldDate, null, null);

    const previousOperation = this.getPreviousOperation(instance, otherOperations);

    if (!this.checkStartTimes(instance)) {
      validationResult.addError(`You are missing a start time and so you can't save your operation until you add one.`, ErrorType.Error);
    }

    const message = await this.checkOperationInAStage(instance);

    if (!!message) {
      validationResult.addError(message, ErrorType.Warning);
    }

    const timeZoneErrors = await this.checkOperationTimesNotLostInZone(cloneDeep(instance), dailyLogDate.dateTime);

    timeZoneErrors.forEach(error => {
      validationResult.addError(error, ErrorType.Error);
    });

    const multipleOffsetErrors = await this.checkForMultipleOffsets(instance);

    multipleOffsetErrors.forEach(error => {
      validationResult.addError(error, ErrorType.Error, ActionType.Timezone);
    });

    if (instance.pausedTimes != null && instance.pausedTimes.length > 0) {
      for (let i = 0; i < instance.pausedTimes.length; i++) {
        const element = instance.pausedTimes[i];
        // const delay = await this.appDataSyncService.getOperationInstance(element.delayApiId);
        const delay = await this.operationService.getProjectSpecificOperationInstance(element.delayApiId);

        if (!delay) {
          continue;
        }

        let endString = this.dateService.setTimeStringFromPtLuxonDate(element.endDate);

        if (!!endString) {
          endString = 'to ' + endString;
        } else {
          endString = '';
        }

        validationResult.addError(`This operation has been paused from ${this.dateService.setTimeStringFromPtLuxonDate(element.startDate)}
        ${endString} due to the delay
        '${delay.projectSpecificOperation.name || delay.projectSpecificOperation.operationSummary.name}'`, ErrorType.Warning);
      }
    }

    const stepEndsValid = this.validateStepEndTimes(instance);

    if (!stepEndsValid || !instance.endTimeToShow) {
      validationResult.addError('You have not provided an end time and so the operation ' +
        'will continue until an end time is entered.', ErrorType.Warning);
    }

    const operationStartCorrect = await this.checkOperationStartTime(instance);

    if (!!operationStartCorrect) {
      validationResult.addError(`Your operation start time (${instance.projectSpecificOperation.name
      || instance.projectSpecificOperation.operationSummary.name}, Start time:
        ${await this.dateService.setFullTimeString(instance.startTime.dateTime)}) is later than one of your steps' start times
        (Step: ${operationStartCorrect.stepInstance.step.name}, Start time: ${await this.dateService.setFullTimeString(operationStartCorrect.time.dateTime)})
        `, ErrorType.Error);
    }

    const nextDay = await this.dateService.incrementDay(cloneDeep(dailyLogDate));
    const startTimeWrongDay = instance.startTime.dateTime >= nextDay.dateTime;
    if (startTimeWrongDay) {
      validationResult.addError(`Your operation start time:
        (${instance.startTimeToShow}) starts on the next day (${instance.startTime.dateTime.toFormat('dd/MM/yyyy')}). The Current
        Daily Log day (${dailyLogDate.dateTime.toFormat('dd/MM/yyyy')}) must be completed before operations can be started on this day.`
        , ErrorType.Error);
    }

    const operationEndCorrect = await this.checkOperationEndTime(instance);

    if (!!operationEndCorrect) {
      validationResult.addError(`Your operation end time (${instance.projectSpecificOperation.name
      || instance.projectSpecificOperation.operationSummary.name}, End time:
        ${await this.dateService.setFullTimeString(instance.endTime.dateTime)}) is earlier than step time (Step:
        ${operationEndCorrect.stepInstance.step.name}, Time:
        ${await this.dateService.setFullTimeString(operationEndCorrect.time.dateTime)})`, ErrorType.Error);
    }

    const operationEndBeforeStart = await this.checkOperationEndsAfterStart(instance);
    if (operationEndBeforeStart) {
      validationResult.addError(`Your operation end time (${instance.projectSpecificOperation.name
      || instance.projectSpecificOperation.operationSummary.name}, End time:
        ${await this.dateService.setFullTimeString(instance.endTime.dateTime)}) is earlier than the Operation Start Time:
        ${await this.dateService.setFullTimeString(instance.startTime.dateTime)})`, ErrorType.Error);
    }

    let difference = null;

    if (type !== OperationType.Delay && !mostRecentTime == null && !mostRecentTime.dateTime == null) {
      difference = Math.abs(this.dateService.millisecondDifference(instance.startTime.dateTime, mostRecentTime.dateTime));
    }

    if (difference == null) {
      difference = 0;
    }

    let dayCount = 0;

    if (!!mostRecentTime && !!mostRecentTime.dateTime) {
      dayCount = await this.calculateDayCount(instance);
    }

    // if no end time then the instance will span infinitely, so no point in adding the below span warnings
    if (instance.endTime && instance.endTime.dateTime) {
      if (dayCount === 2) {
        const toDate = await this.dateService.addDuration(instance.startTime.dateTime, {day: dayCount - 1}, true);
        validationResult = await this.addSpanningValidationWarning(validationResult, dayCount, instance, toDate);
      }

      if (dayCount > 2) {
        const operationEndOrCancelledDate = instance.cancelledTime ? instance.cancelledTime : instance.endTime.dateTime;
        const endOfCurrentDate = await this.timeZoneService.getEndOfDayForDate(dailyLogDate.dateTime);
        const toDate = await this.dateService.addDuration(instance.startTime.dateTime, {day: dayCount - 1}, true);
        const toDateStartOfDay = await this.timeZoneService.getStartOfDayForDate(toDate);

        // (endOfCurrentDate.toMillis() != toDateStartOfDay.toMillis()) means that the user can still make the operation span into the next day.
        // While we don't want to normally let them make an operation span over 2, they can for example, start on the 15th,
        // then on the 16th, they should be able to make it span onto the 17th as it's the next log day - while still stopping them from being able
        // to span it to the 17th when editing the 15th
        // only show this error if not in implementer mode
        if (endOfCurrentDate.toMillis() != toDateStartOfDay.toMillis() && operationEndOrCancelledDate >= endOfCurrentDate && !AppConfig.settings.isImplementerEnvironment) {
          validationResult.addError(`Your current step configuration will result in this operation spanning
      ${dayCount.toString()} days (${!instance.startTime.dateTime
              ? 'Not Specified'
              : instance.startTime.dateTime.toFormat('dd/MM/yyyy')}
        to ${!instance.startTime.dateTime
              ? 'Not Specified'
              : toDate.toFormat('dd/MM/yyyy')}),
        this is not allowed. If you want your operation to span ${dayCount.toString()} days, you should leave it open ended.`,
            ErrorType.Error);
        } else {
          validationResult = await this.addSpanningValidationWarning(validationResult, dayCount, instance, toDate);
        }
      }
    }

    const delayOverlaps = await this.checkDelayOverlap(instance);

    if (delayOverlaps != null && delayOverlaps.length !== 0) {
      for (const overlap of delayOverlaps) {
        validationResult.addError(`Saving this operation will cause an overlap between a delay (Delay:
          ${overlap.operationInstance.projectSpecificOperation.name
        || overlap.operationInstance.projectSpecificOperation.operationSummary.name}, start time:
          ${this.dateService.setTimeStringFromPtLuxonDate(overlap.startTime)}, end time:
          ${this.dateService.setTimeStringFromPtLuxonDate(overlap.endTime)}) and your operation (Operation:
          ${instance.projectSpecificOperation.name || instance.projectSpecificOperation.operationSummary.name}, start time:
          ${instance.startTimeToShow}, end time:
          ${instance.endTimeToShow})`, ErrorType.Warning);
      }
    }

    if (!instance.isConcurrentOperation) {
      if (!mostRecentTime == null && !mostRecentTime.dateTime == null) {
        await this.contractualOperationValidationService
          .checkContractualOperation({
            instance: instance,
            previousOperation: previousOperation,
            mostRecentTime: mostRecentTime,
            difference: difference
          }, validationResult);
      }
    } else {
      await this.concurrentOperationValidationService.findGapDuringTimeSpan(
        instance.startTime,
        instance.endTime,
        validationResult,
        dailyLogDate.dateTime,
        instance.id || instance.localId);
    }

    if (instance.projectSpecificOperation.operationSummary.classificationName !== 'Delay') {
      const operations = await this.operationService.getAllProjectSpecificOperationInstances();
      const dailyLogDateClone = cloneDeep(dailyLogDate);
      const hierarchyCollisions = await this.detectHierarchyMisplacements(instance, operations, dailyLogDateClone, null);

      for (let i = 0; i < hierarchyCollisions.length; i++) {
        const element = hierarchyCollisions[i];

        if (element.higherInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay'
          || element.lowerInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay') {
          continue;
        }

        await validationResult.addError(`Saving this operation will cause a contractual hierarchy collision with the
        ${element.higherInstance.isConcurrentOperation ? 'concurrent' : 'contractual'} operation
        ${element.higherInstance.projectSpecificOperation.name || element.higherInstance.projectSpecificOperation.operationSummary.name}
        (Start Time: ${this.dateService.setTimeStringFromPtLuxonDate(element.higherInstance.startTime)}, End Time:
        ${element.higherInstance.endTime && element.higherInstance.endTime.dateTime
          ? this.dateService.setTimeStringFromPtLuxonDate(element.higherInstance.endTime)
          : 'No End'})
        which has ${element.higherInstance.projectSpecificOperation.contractualPriority
          ? 'a contractual priority of ' + element.higherInstance.projectSpecificOperation.contractualPriority
          : 'no contractual priority'}, and the
        ${element.lowerInstance.isConcurrentOperation ? 'concurrent' : 'contractual'} operation
        ${element.lowerInstance.projectSpecificOperation.name || element.lowerInstance.projectSpecificOperation.operationSummary.name}
        (Start Time: ${this.dateService.setTimeStringFromPtLuxonDate(element.lowerInstance.startTime)}, End Time:
        ${element.lowerInstance.endTime && element.lowerInstance.endTime.dateTime
          ? this.dateService.setTimeStringFromPtLuxonDate(element.lowerInstance.endTime)
          : 'No End'})
        which has ${element.lowerInstance.projectSpecificOperation.contractualPriority
          ? 'a contractual priority of ' + element.lowerInstance.projectSpecificOperation.contractualPriority
          : 'no contractual priority'}.`, ErrorType.Warning);
      }
    }

    const stepErrors = this.checkStepTimes(instance);

    stepErrors.forEach(element => {
      // todo - this was using inAppOffset - think we need to check for if this works when adding dates with multiple offsets
      if (element.stepInstance.startTime.dateTime && element.stepInstance.startTime.offsetNotNull() && element.stepInstance.endTime.dateTime && element.stepInstance.endTime.offsetNotNull()) {
        validationResult.addError(`For step '${element.stepInstance.step.name}' your
      end time '${element.stepInstance.endTimeToShow}
       (${element.stepInstance.endTime.dateTime.offset >= 0 ? '+' : ''}${element.stepInstance.endTime.dateTime.offset / 60})' is before your
      start time '${element.stepInstance.startTimeToShow}
      (${element.stepInstance.startTime.dateTime.offset >= 0 ? '+' : ''}${element.stepInstance.startTime.dateTime.offset / 60})'. This is not
      allowed.`, ErrorType.Error, ActionType.OperationRule);
      }
    });

    const stepSpanErrors = this.checkStepSpans(instance);

    stepSpanErrors.forEach(element => {
      validationResult.addError(`For step '${element.stepInstance.step.name}'
          your end time '${element.stepInstance.endTimeToShow}
           (${element.stepInstance.endTime.dateTime.offset >= 0 ? '+' : ''}${element.stepInstance.endTime.dateTime.offset / 60})'
          is equal to the step start time '${element.stepInstance.startTimeToShow}
          (${element.stepInstance.startTime.dateTime.offset >= 0 ? '+' : ''}${element.stepInstance.startTime.dateTime.offset / 60})'.
          This step has been assigned a duration by the Operations team, and is therefore not allowed to have
          a duration of 0 minutes.`, ErrorType.Error, ActionType.OperationRule);
    });

    if ((instance.projectSpecificOperation.operationSummary.classificationName === 'Delay' || instance.isInterruption)
      && instance.startTime.dateTime && instance.endTime.dateTime
      && instance.startTime.dateTime.toMillis() === instance.endTime.dateTime.toMillis()) {
      validationResult.addError(`Delays and Interruptions must have a span greater than 0`, ErrorType.Error);
    }

    if (this.isRequiredStartCommentMissing(instance)) {
      validationResult.addError(
        `A start comment is required for delays and interruptions.`,
        ErrorType.Error,
        ActionType.OperationRule);
    }

    const operationCommentsErrors = await this.commentTimesValid(instance);

    operationCommentsErrors.forEach(error => {
      validationResult.addError(error.errorMessage, ErrorType.Error);
    });

    return validationResult;
  }

  isStartCommentRequired(instance: DailyLogOperationInstance): boolean {
    return !!instance
      && !!instance.projectSpecificOperation
      && !!instance.projectSpecificOperation.operationSummary
      && (instance.projectSpecificOperation.operationSummary.classificationName === 'Delay'
        || instance.isInterruption);
  }

  isRequiredStartCommentMissing(instance: DailyLogOperationInstance): boolean {
    return this.isStartCommentRequired(instance)
      && (!instance.startComment || !instance.startComment.data || !instance.startComment.data.trim());
  }

  isRequiredStartCommentComplete(instance: DailyLogOperationInstance): boolean {
    return this.isStartCommentRequired(instance) && !this.isRequiredStartCommentMissing(instance);
  }

  private addSpanningValidationWarning(validationResult: ValidationResult, dayCount: number, instance: DailyLogOperationInstance, toDate: DateTime): ValidationResult {
    validationResult.addError(`Your current step configuration will result in this operation spanning
       ${dayCount.toString()} days (${!instance.startTime.dateTime
      ? 'Not Specified'
      : instance.startTime.dateTime.toFormat('dd/MM/yyyy')}

      to ${!instance.startTime.dateTime
      ? 'Not Specified'
      : toDate.toFormat('dd/MM/yyyy')})
      , are you sure you want to do this?`, ErrorType.Warning);

    return validationResult;
  }

  async checkOperationInAStage(instance: DailyLogOperationInstance, setupData?: SetupChangeLogRecord[]): Promise<string> {
    const stageSetups: StageStartAndEnd[] = [];

    for (const stageId of instance.projectSpecificOperation.stageIds) {
      const stages = await this.setupService.getTimesForStage(stageId, cloneDeep(setupData));
      for (const stage of stages) {
        stageSetups.push(stage);
      }
    }

    let startAllowed = false;

    for (const stageSetup of stageSetups) {
      const inTime = this.dateService.timeInRangeInclusive(
        stageSetup.startTime.dateTime,
        stageSetup.endTime ? stageSetup.endTime.dateTime : null,
        instance.startTime.dateTime);

      if (inTime) {
        startAllowed = true;
        break;
      }
    }

    if (stageSetups && stageSetups.length > 0 && !startAllowed) {
      return 'Your operation start time falls outside of the necessary stage';
    }

    let endAllowed = false;

    for (const stageSetup of stageSetups) {
      const inTime = this.dateService.timeInRangeInclusive(
        stageSetup.startTime.dateTime,
        stageSetup.endTime ? stageSetup.endTime.dateTime : null,
        instance.cancelledTime ? instance.cancelledTime : instance.endTime.dateTime);

      if (inTime) {
        endAllowed = true;
        break;
      }
    }

    if (stageSetups && stageSetups.length > 0 && !endAllowed) {
      return 'Your operation end time falls outside of the necessary stage';
    }

    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances.filter(e => !e.exclude);

    if (!steps) {
      return null;
    }

    let stepsAllowed = true;

    for (const step of steps) {
      let stepStartAllowed = false;
      let stepEndAllowed = false;

      for (const stageSetup of stageSetups) {
        if (step.startTime) {
          const startInTime = this.dateService.timeInRangeInclusive(
            stageSetup.startTime.dateTime,
            stageSetup.endTime
              ? stageSetup.endTime.dateTime
              : null,
            // if operation cancelled we dont care about these step times, as long as the cancelled time is smaller than the step time
            instance.cancelledTime && (!step.startTime.dateTime || instance.cancelledTime.toMillis() < step.startTime.dateTime.toMillis())
              ? instance.cancelledTime
              : step.startTime.dateTime);

          if (startInTime) {
            stepStartAllowed = true;
          }
        }

        if (step.endTime) {
          const endInTime = this.dateService.timeInRangeInclusive(
            stageSetup.startTime.dateTime,
            stageSetup.endTime
              ? stageSetup.endTime.dateTime
              : null,
            // if operation cancelled we dont care about these step times, as long as the cancelled time is smaller than the step time
            instance.cancelledTime && (!step.endTime.dateTime || instance.cancelledTime.toMillis() < step.endTime.dateTime.toMillis())
              ? instance.cancelledTime
              : step.endTime.dateTime);

          if (endInTime) {
            stepEndAllowed = true;
          }
        }
      }

      if (!stepStartAllowed || !stepEndAllowed) {
        stepsAllowed = false;
        break;
      }
    }

    if (!stepsAllowed) {
      return 'One or more of your operation steps fall outside of the necessary stage';
    }
  }

  async checkOperationTimesNotLostInZone(instance: DailyLogOperationInstance, currentDailyLogDate: DateTime): Promise<string[]> {
    const errors = [] as string[];

    if (instance.startTime && instance.startTime.isLostInTime && instance.startTime.isLostInTime.result) {
      errors.push(`Operation start time ` + instance.startTimeToShow +
        ` isn't allowed due to a time zone change from ` + instance.startTime.isLostInTime.timeZone.timeOfChangeToShow + ' to '
        + instance.startTime.isLostInTime.timeZone.dateToShow);
    }

    if (!!instance.stepCollectionInstance) {
      for (const stepInstance of instance.stepCollectionInstance.stepInstances) {
        if (stepInstance.startTime && stepInstance.startTime.dateTime) {
          if (stepInstance.startTime && stepInstance.startTime.isLostInTime && stepInstance.startTime.isLostInTime.result) {
            errors.push(`Step '` + stepInstance.step.name + `' start time ` + stepInstance.startTimeToShow +
              ` isn't allowed due to a time zone change from ` + stepInstance.startTime.isLostInTime.timeZone.timeOfChangeToShow + ' to '
              + stepInstance.startTime.isLostInTime.timeZone.dateToShow);
          }
        }

        if (stepInstance.endTime && stepInstance.endTime.dateTime) {
          if (stepInstance.endTime.isLostInTime && stepInstance.endTime.isLostInTime.result) {
            errors.push(`Step '` + stepInstance.step.name + `' end time ` + stepInstance.endTimeToShow +
              ` isn't allowed due to a time zone change from ` + stepInstance.endTime.isLostInTime.timeZone.timeOfChangeToShow + ' to '
              + stepInstance.endTime.isLostInTime.timeZone.dateToShow);
          }
        }
      }
    }

    if (instance.endTime && instance.endTime.dateTime) {
      if (instance.endTime && instance.endTime.isLostInTime && instance.endTime.isLostInTime.result) {
        errors.push(`Operation end time ` + instance.endTimeToShow + ` isn't allowed due to a time zone change from `
          + instance.endTime.isLostInTime.timeZone.timeOfChangeToShow + ' to ' + instance.endTime.isLostInTime.timeZone.dateToShow);
      }

    }

    return errors;
  }

  async checkForMultipleOffsets(instance: DailyLogOperationInstance): Promise<string[]> {
    const errors = [] as string[];
    const operationStartMultipleOffsets = instance.startTime && instance.startTime.multipleOffsets && instance.startTime.multipleOffsets.result
      && instance.startTime.multipleOffsets.selectedOffsetMinutes == null;
    const stepMultipleOffsets = instance.stepCollectionInstance && instance.stepCollectionInstance.stepInstances.some(
      e => !e.exclude
        && (e.startTime && e.startTime.dateTime && e.startTime.multipleOffsets && e.startTime.multipleOffsets.result && e.startTime.multipleOffsets.selectedOffsetMinutes == null)
        || (e.endTime && e.endTime.dateTime && e.step.defaultSecondsSpan !== 0 && e.secondsSpan !== 0 && e.endTime.multipleOffsets
          && e.endTime.multipleOffsets.result && e.endTime.multipleOffsets.selectedOffsetMinutes == null));
    const operationEndMultipleOffsets = instance.endTime && instance.endTime.offsetNull();

    if (operationStartMultipleOffsets || stepMultipleOffsets || operationEndMultipleOffsets) {
      errors.push(`One or more operation times could have multiple possible offsets due to a time zone change. All time offsets must be
      chosen before you can save the operation.`);
    }

    return errors;
  }

  getPreviousOperation(instance: DailyLogOperationInstance, operations: DailyLogOperationInstance[]): DailyLogOperationInstance {
    if (!instance.startTime.dateTime) {
      return null;
    }

    let viableOperations = operations
      .filter(p => p.isConcurrentOperation === false);

    viableOperations = viableOperations
      .filter(p => !!p.endTime.dateTime);

    viableOperations = viableOperations
      .filter(p => p.endTime.dateTime <= instance.startTime.dateTime);

    if (viableOperations.length < 1) {
      return null;
    }

    let previousOperation = viableOperations[0];

    viableOperations.forEach(operation => {
      if (previousOperation.endTime.dateTime < operation.endTime.dateTime) {
        previousOperation = operation;
      }
    });

    return previousOperation;
  }

  checkStepTimes(instance: DailyLogOperationInstance): StepTimeError[] {
    const errors = [] as StepTimeError[];
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return [];
    }

    steps.forEach(element => {
      if (!element.startTime || !element.startTime.dateTime || !element.endTime || !element.endTime.dateTime) {
        return;
      }

      if (element.startTime.dateTime > element.endTime.dateTime) {
        const error = new StepTimeError();
        error.stepInstance = element;
        error.time = element.startTime;
        errors.push(error);
      }
    });

    return errors;
  }

  checkStepSpans(instance: DailyLogOperationInstance): StepTimeError[] {
    const errors = [] as StepTimeError[];
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return [];
    }

    steps.forEach(element => {
      if (!element.startTime || !element.startTime.dateTime || !element.endTime || !element.endTime.dateTime) {
        return;
      }

      if (element.step.defaultSecondsSpan !== 0 && element.startTime.dateTime.toMillis() === element.endTime.dateTime.toMillis()) {
        const error = new StepTimeError();
        error.stepInstance = element;
        error.time = element.startTime;
        errors.push(error);
      }
    });

    return errors;
  }

  async commentTimesValid(operationInstance: DailyLogOperationInstance): Promise<CommentTimeError[]> {
    const errors = [] as CommentTimeError[];

    if (operationInstance.comments == null) {
      return errors;
    }

    for (let i = 0; i < operationInstance.comments.length; i++) {
      const comment = operationInstance.comments[i];
      const dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      const startTime = comment.startTimeToShow != null && comment.startTimeToShow.trim() !== ''
        ? comment.startTimeDate && comment.startTimeDate.dateTime
          ? comment.startTimeDate
          : dailyLogDate
        : new PtLuxonDate(null, true);
      const startTimeInvalid = startTime && startTime
        ? (operationInstance.endTime.dateTime != null && startTime.dateTime > operationInstance.endTime.dateTime
          || operationInstance.startTime.dateTime != null && startTime.dateTime < operationInstance.startTime.dateTime)
        : false;

      if (startTimeInvalid) {
        const error = new CommentTimeError();
        const commentData = comment.data.length < 15
          ? '\'' + comment.data
          : 'beginning \'' + comment.data.substring(0, 15).concat('...');

        error.invalidComment = comment;
        error.errorMessage = 'Comment \'' + commentData + '\' has a time that falls ' +
          'outside of the operation times.';

        errors.push(error);
      }

      if (startTime.isLostInTime && startTime.isLostInTime.result) {
        const error = new CommentTimeError();
        const commentData = comment.data.length < 15 ? comment.data : comment.data.substring(0, 15).concat('...');

        error.invalidComment = comment;
        error.errorMessage = `Comment '${commentData}' has an invalid time '${comment.startTimeToShow}' due a time zone change
          from '${startTime.isLostInTime.timeZone.timeOfChangeToShow}' to '${startTime.isLostInTime.timeZone.dateToShow}' `;

        errors.push(error);
      }
    }

    return errors;
  }

  async checkOperationStartTime(operationInstance: DailyLogOperationInstance): Promise<StepTimeError> {
    const error = new StepTimeError();

    if (!operationInstance.startTime || operationInstance.startTime.dateTime == null) {
      return null;
    }

    const steps = operationInstance.stepCollectionInstance != null
      ? operationInstance.stepCollectionInstance.stepInstances
      : null;

    if (!steps) {
      return null;
    }

    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].startTime || steps[i].startTime.dateTime == null || steps[i].exclude === true) {
        continue;
      }

      const stepStartDate = steps[i].startTime.dateTime;
      const stepStartTime = !!stepStartDate ? stepStartDate.toMillis() : null;

      const operationStartDate = cloneDeep(operationInstance.startTime.dateTime);
      const operationStartTime = !!operationStartDate ? operationStartDate.toMillis() : null;

      const difference = Math.abs(stepStartTime - operationStartTime);
      if (difference > 1000 && stepStartTime < operationStartTime) {
        error.time = steps[i].startTime;
        error.stepInstance = steps[i];
        return error;
      }
    }

    return null;
  }

  async checkOperationEndTime(operationInstance: DailyLogOperationInstance): Promise<StepTimeError> {
    const error = new StepTimeError();

    if (!operationInstance.endTime || !operationInstance.endTime.dateTime || operationInstance.cancelledTime != null) {
      return null;
    }

    const newEndTime = operationInstance.endTime;

    const steps = operationInstance.stepCollectionInstance != null
      ? operationInstance.stepCollectionInstance.stepInstances
      : null;

    if (!steps) {
      return null;
    }

    for (let i = 0; i < steps.length; i++) {
      if (steps[i].exclude === true) {
        continue;
      }

      if (steps[i].endTime.dateTime > newEndTime.dateTime) {
        error.time = steps[i].endTime;
        error.stepInstance = steps[i];
        return error;
      }
    }

    return null;
  }

  checkOperationEndsAfterStart(operationInstance: DailyLogOperationInstance): StepTimeError {
    const error = new StepTimeError();

    if (!operationInstance.endTime || !operationInstance.endTime.dateTime) {
      return null;
    }

    if (operationInstance.startTime.dateTime > operationInstance.endTime.dateTime) {
      error.time = operationInstance.endTime;
      return error;
    }

    return null;
  }

  checkStartTimes(operationInstance: DailyLogOperationInstance): boolean {
    if (operationInstance.startTime.dateTime == null || isNaN(operationInstance.startTime.dateTime.toMillis()) || operationInstance.startTimeToShow == null) {
      return false;
    }

    const steps = operationInstance.stepCollectionInstance != null
      ? operationInstance.stepCollectionInstance.stepInstances
      : null;

    if (!steps) {
      return true;
    }

    for (let i = 0; i < steps.length; i++) {
      if ((!steps[i].startTimeToShow || steps[i].startTimeToShow === '') && !!steps[i].endTimeToShow) {
        return false;
      }
    }

    return true;
  }

  validateStepEndTimes(operationInstance: DailyLogOperationInstance): boolean {
    const steps = operationInstance.stepCollectionInstance != null ? operationInstance.stepCollectionInstance.stepInstances : null;

    if (!steps) {
      return true;
    }

    steps.forEach(element => {
      if (!element.endTimeToShow) {
        return false;
      }
    });

    return true;
  }

  // if steps are on a different day than another step, or instance start/end is on another day, or one of these are null (so open ended)
  // then we want to display the dates on the editor
  async multipleDaysExist(instance: DailyLogOperationInstance): Promise<boolean> {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!!steps) {
      for (let i = 0; i < steps.length; i++) {

        const stepStartIsSame = instance.startTime.dateTime && instance.startTime.offsetNotNull() && steps[i].startTime.dateTime && steps[i].startTime.offsetNotNull()
        || (!steps[i].startTime || !steps[i].startTime.dateTime)
          ? await this.dateService.compareDayOffsetsPreApplied(instance.startTime.dateTime, steps[i].startTime.dateTime)
          : true;
        const stepEndIsSame = instance.startTime.dateTime && instance.startTime.offsetNotNull() && instance.endTime.dateTime && steps[i].endTime.offsetNotNull()
        || (!steps[i].endTime || !steps[i].endTime.dateTime)
          ? await this.dateService.compareDayOffsetsPreApplied(instance.startTime.dateTime, steps[i].endTime.dateTime)
          : true;

        if (!stepStartIsSame || !stepEndIsSame) {
          return true;
        }
      }
    }

    const instanceEndIsSame = instance.startTime.dateTime && instance.startTime.offsetNotNull() && instance.endTime.dateTime && instance.endTime.offsetNotNull()
    || (!instance.endTime || !instance.endTime.dateTime)
      ? await this.dateService.compareDayOffsetsPreApplied(instance.startTime.dateTime, instance.endTime.dateTime)
      : true;

    return (instance.startTime.dateTime && instance.startTime.offsetNotNull() && instance.endTime.dateTime && instance.endTime.offsetNotNull() && instance.endTime.dateTime < instance.startTime.dateTime)
      || !instanceEndIsSame;
  }

  async calculateDayCount(instance: DailyLogOperationInstance): Promise<number> {
    const startDay = cloneDeep(instance.startTime.dateTime.ordinal);
    let currentDay = cloneDeep(startDay);

    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!!steps) {
      for (const element of steps) {
        if (element.startTime && element.endTime) {
          if (instance.cancelledTime && element.startTime.dateTime && element.startTime.dateTime > instance.cancelledTime) {
            continue;
          }

          let latestDay = cloneDeep(element.startTime.dateTime ? element.startTime.dateTime.ordinal : -1);

          if (latestDay > currentDay) {
            currentDay = cloneDeep(latestDay);
          }


          if (instance.cancelledTime && element.endTime.dateTime && element.endTime.dateTime > instance.cancelledTime) {
            continue;
          }

          latestDay = cloneDeep(element.endTime.dateTime ? element.endTime.dateTime.ordinal : -1);

          if (latestDay > currentDay) {
            currentDay = cloneDeep(latestDay);
          }
        }
      }
    }

    const lastDay = cloneDeep(instance.cancelledTime
      ? instance.cancelledTime.ordinal
      : instance.endTime.dateTime
        ? instance.endTime.dateTime.ordinal
        : null);

    if (lastDay > currentDay) {
      currentDay = cloneDeep(lastDay);
    }

    return (currentDay - startDay) + 1;
  }

  async checkDelayOverlap(newOperation: DailyLogOperationInstance): Promise<StartAndEnd[]> {
    const delays = cloneDeep(await this.operationService.getDelays());

    const overlapDates = [];

    const newEndTime = await this.dateService.setPtLuxonDateFromUserEnteredString(newOperation.endTimeToShow, newOperation.endTime.dateTime);
    const newStartTime = await this.dateService.setPtLuxonDateFromUserEnteredString(newOperation.startTimeToShow, newOperation.startTime.dateTime);

    if (newEndTime == null || newEndTime.dateTime == null || newStartTime == null || newStartTime.dateTime == null) {
      return;
    }

    delays.forEach(element => {
      element.projectSpecificOperation.startTime = PtLuxonDate.assignPtLuxonDate(element.projectSpecificOperation.startTime);
      element.projectSpecificOperation.endTime = PtLuxonDate.assignPtLuxonDate(element.projectSpecificOperation.endTime);

      if (element.id === newOperation.id) {
        return;
      }

      if (element.projectSpecificOperation.startTime.dateTime == null || element.projectSpecificOperation.endTime.dateTime == null) {
        return;
      }

      let overlapping = this.operationService.operationInTimeExclusive(
        element.projectSpecificOperation.startTime.dateTime,
        element.projectSpecificOperation.endTime.dateTime,
        newStartTime.dateTime,
        newEndTime.dateTime);

      if (!overlapping) {
        overlapping = this.operationService.operationInTimeExclusive(
          newStartTime.dateTime,
          newEndTime.dateTime,
          element.projectSpecificOperation.startTime.dateTime,
          element.projectSpecificOperation.endTime.dateTime);
      }

      /*const beforeOldOp = (newStartTime.dateTime
        <= element.operation.startTime.dateTime && newEndTime.dateTime
        <= element.operation.startTime.dateTime);

      const afterOldOp = (newStartTime.dateTime
        >= element.operation.endTime.dateTime && (newEndTime.dateTime
          >= element.operation.endTime.dateTime
          || newEndTime.dateTime <= element.operation.startTime.dateTime));*/

      if (overlapping) {
        const overlapDate = new StartAndEnd();
        overlapDate.startTime = element.operation.startTime;
        overlapDate.endTime = element.operation.endTime;
        overlapDate.operationInstance = element.operation;
        overlapDate.operationInstanceId = element.id;
        overlapDates.push(overlapDate);
        return;
      }
    });

    return overlapDates;
  }

  async detectHierarchyMisplacements(
    instance: DailyLogOperationInstance,
    operations: DailyLogOperationInstance[],
    dailyLogDate: PtLuxonDate,
    endOfDay?: PtLuxonDate,
  ): Promise<HierarchyCollision[]> {
    if (!endOfDay || !endOfDay.dateTime) {
      endOfDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);
    }

    const collisions = [] as HierarchyCollision[];
    const otherContractualTimes = await this.contractualOperationValidationService.getOtherContractualTimes(instance, null, null, operations, dailyLogDate);
    const overlapContractualTimeGroup = new ContractualTimeGroup();

    overlapContractualTimeGroup.isContractual = true;
    overlapContractualTimeGroup.times = otherContractualTimes.filter(time => {
      let found = this.operationService.contractualHierarchyOperationInTime(
        instance.startTime.dateTime,
        instance.endTime.dateTime,
        time.startTime.dateTime,
        time.endTime.dateTime);

      if (!found) {
        found = this.operationService.contractualHierarchyOperationInTime(
          time.startTime.dateTime,
          time.endTime.dateTime,
          instance.startTime.dateTime,
          instance.endTime.dateTime);
      }
      return found;
    });

    const otherConcurrentTimes = await this.contractualOperationValidationService.getOtherContractualTimes(instance, null, true, operations, dailyLogDate);
    const overlapConcurrentTimeGroup = new ContractualTimeGroup();

    overlapConcurrentTimeGroup.isContractual = true;
    overlapConcurrentTimeGroup.times = otherConcurrentTimes.filter(time => {
      let found = this.operationService.contractualHierarchyOperationInTime(
        instance.startTime.dateTime,
        instance.endTime.dateTime,
        time.startTime.dateTime,
        time.endTime.dateTime);

      if (!found) {
        found = this.operationService.contractualHierarchyOperationInTime(
          time.startTime.dateTime,
          time.endTime.dateTime,
          instance.startTime.dateTime,
          instance.endTime.dateTime);
      }
      return found;
    });

    const operationContractualTimes = await this.contractualOperationValidationService.getTimesForOperation(instance, false, endOfDay, dailyLogDate);
    const operationConcurrentTimes = await this.contractualOperationValidationService.getTimesForOperation(instance, true, endOfDay, dailyLogDate);

    for (const contractualTime of operationContractualTimes.times) {
      for (const concurrentTime of overlapConcurrentTimeGroup.times) {
        let found = this.operationService.contractualHierarchyOperationInTime(
          contractualTime.startTime.dateTime,
          contractualTime.endTime.dateTime,
          concurrentTime.startTime.dateTime,
          concurrentTime.endTime.dateTime);

        if (!found) {
          found = this.operationService.contractualHierarchyOperationInTime(
            concurrentTime.startTime.dateTime,
            concurrentTime.endTime.dateTime,
            contractualTime.startTime.dateTime,
            contractualTime.endTime.dateTime);
        }

        if (found) {
          if (contractualTime.operationInstance != null
            && concurrentTime.operationInstance != null
            && this.hierarchyClashDetected(contractualTime, concurrentTime)) {

            const collision = new HierarchyCollision();
            collision.higherInstance = concurrentTime.operationInstance;
            collision.higherInstanceTempId = concurrentTime.operationInstanceId;
            collision.lowerInstance = contractualTime.operationInstance;
            collision.lowerInstanceTempId = contractualTime.operationInstanceId;

            let collisionTime = this.operationService.findOverlapZone(
              contractualTime.startTime.dateTime,
              contractualTime.endTime.dateTime,
              concurrentTime.startTime.dateTime,
              concurrentTime.endTime.dateTime);

            if (!collisionTime) {
              collisionTime = this.operationService.findOverlapZone(
                concurrentTime.startTime.dateTime,
                concurrentTime.endTime.dateTime,
                contractualTime.startTime.dateTime,
                contractualTime.endTime.dateTime);
            }

            let collisionNotOnCurrentLogDate = false;
            if ((collisionTime.startTime && collisionTime.startTime.dateTime && collisionTime.endTime && collisionTime.endTime.dateTime) && (
              (collisionTime.endTime.dateTime < dailyLogDate.dateTime) // collision entirely on previous day
              || (collisionTime.startTime.dateTime >= endOfDay.dateTime) // collision entirely on next day
            )) {
              collisionNotOnCurrentLogDate = true;
            }

            collision.collisionStart = collisionTime.startTime;
            collision.collisionEnd = collisionTime.endTime;

            // todo - check if needed
            // await collision.collisionStart.setLocalDate(this.timeZoneService, null);
            // await collision.collisionEnd.setLocalDate(this.timeZoneService, null);

            const sameCollisions = collisions.filter(p =>
              p.lowerInstance === collision.lowerInstance
              && p.higherInstance === collision.higherInstance);

            if (!collisionNotOnCurrentLogDate && sameCollisions.length === 0) {
              collisions.push(collision);
            }
          }
        }
      }
    }

    operationConcurrentTimes.times.forEach(concurrentTime => {
      overlapContractualTimeGroup.times.forEach(contractualTime => {
        let found = this.operationService.contractualHierarchyOperationInTime(
          concurrentTime.startTime.dateTime,
          concurrentTime.endTime.dateTime,
          contractualTime.startTime.dateTime,
          contractualTime.endTime.dateTime);

        if (!found) {
          found = this.operationService.contractualHierarchyOperationInTime(
            contractualTime.startTime.dateTime,
            contractualTime.endTime.dateTime,
            concurrentTime.startTime.dateTime,
            concurrentTime.endTime.dateTime);
        }

        if (found) {
          if (contractualTime.operationInstance != null
            && concurrentTime.operationInstance != null
            && this.hierarchyClashDetected(contractualTime, concurrentTime)) {
            const collision = new HierarchyCollision();
            collision.higherInstance = concurrentTime.operationInstance;
            collision.lowerInstance = contractualTime.operationInstance;
            collision.higherInstanceTempId = concurrentTime.operationInstanceId;
            collision.lowerInstanceTempId = contractualTime.operationInstanceId;

            let collisionTime = this.operationService.findOverlapZone(
              concurrentTime.startTime.dateTime,
              concurrentTime.endTime.dateTime,
              contractualTime.startTime.dateTime,
              contractualTime.endTime.dateTime);

            if (!collisionTime) {
              collisionTime = this.operationService.findOverlapZone(
                contractualTime.startTime.dateTime,
                contractualTime.endTime.dateTime,
                concurrentTime.startTime.dateTime,
                concurrentTime.endTime.dateTime);
            }

            collision.collisionStart = collisionTime.startTime;
            collision.collisionEnd = collisionTime.endTime;

            const sameCollisions = collisions.filter(p =>
              p.lowerInstance === collision.lowerInstance
              && p.higherInstance === collision.higherInstance);

            if (sameCollisions.length === 0) {
              collisions.push(collision);
            }
          }
        }
      });
    });

    return collisions;
  }

  private hierarchyClashDetected(contractualTime: StartAndEnd, concurrentTime: StartAndEnd) {
    return concurrentTime.operationInstance.projectSpecificOperation.contractualPriority // null check or it will give false detection
      && (concurrentTime.operationInstance.projectSpecificOperation.contractualPriority
      < contractualTime.operationInstance.projectSpecificOperation.contractualPriority)
      || this.nullHierarchyClashDetected(contractualTime, concurrentTime);
  }

  private nullHierarchyClashDetected(contractualTime: StartAndEnd, concurrentTime: StartAndEnd) {
    // a null contractual priority should be treated as the lowest priority possible
    // i.e - it only triggers a hierarchy clash if a contractual null hierarchy is logged against a concurrent >0 hierarchy
    return concurrentTime.operationInstance.projectSpecificOperation.contractualPriority > 0
      && contractualTime.operationInstance.projectSpecificOperation.contractualPriority == null;
  }
}



// WEBPACK FOOTER //
// ./src/services/operations/operation-validator.service.ts